import { Box, Tab, Tabs } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { GetProductTagDto } from "src/services/generated-api";
import { resolveLocalizedName } from "src/utils/resolve-localized-name";

const ResponsiveTabs = ({
  tags,
  selectedTag,
  handleChangeTag,
  mode,
  language,
}: {
  tags: GetProductTagDto[];
  selectedTag: GetProductTagDto | null;
  handleChangeTag: (event: React.SyntheticEvent, newValue: string) => void;
  mode: string;
  language: string;
}) => {
  const currentLocale = useLocale();
  const [variant, setVariant] = useState<"fullWidth" | "scrollable">(
    "fullWidth"
  );
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the Tabs container

  const updateVariant = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const childNodes = containerRef.current.querySelectorAll('[role="tab"]');
      let totalTabsWidth = 0;

      childNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          totalTabsWidth += node.offsetWidth;
        }
      });
      // Switch to 'scrollable' if tabs are wider than the container, otherwise 'fullWidth'
      if (totalTabsWidth - 10 > containerWidth) {
        setVariant("scrollable");
      } else {
        setVariant("fullWidth");
      }
    }
  };

  useEffect(() => {
    // Update variant on mount and window resize
    updateVariant();
    window.addEventListener("resize", updateVariant);

    // Cleanup event listener
    return () => window.removeEventListener("resize", updateVariant);
  }, [tags]); // You might need to adjust dependencies based on your props

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider" }} ref={containerRef}>
      <Tabs
        value={selectedTag ? selectedTag.id : false}
        onChange={handleChangeTag}
        variant={variant}
        scrollButtons="auto"
        aria-label="product tags"
        sx={{ mb: 2 }}
      >
        {tags.map((tag_i) => (
          <Tab
            label={resolveLocalizedName(
              tag_i.name as Record<string, string> | undefined,
              [currentLocale, language, "si", "en"]
            )}
            value={tag_i.id}
            key={tag_i.id}
            sx={{
              textTransform: "none",
              fontWeight: selectedTag?.id === tag_i.id ? "bold" : "normal",
              color: mode === "dark" ? "#fff" : "#000",
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default ResponsiveTabs;
